import React, { MouseEvent, ReactNode, useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Button, ClickAwayListener, Fade, Popper, Stack, Typography } from '@mui/material'
import { thinScrollbarSx } from '../../../style/scrollbar'
import { OverflowTooltipWrapper } from '../OverflowTooltipWrapper/OverflowTooltipWrapper'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Checkbox from '@mui/material/Checkbox'
import { ReactComponent as CheckedIcon } from '../../../assets/images/checkbox-checked.svg'
import { ReactComponent as IndeterminateIcon } from '../../../assets/images/checkbox-indeterminate.svg'
import { ReactComponent as UncheckedIcon } from '../../../assets/images/checkbox-unchecked.svg'
import { ReactComponent as RightArrowIcon } from '../../../assets/images/arrows/right.svg'
import { PulsingCircle } from '../PulsingCircle'
import { SearchInput } from '../SearchInput'
import { useEscapeKey } from '../../hooks/useEscapeKey'

const CheckboxUncheckedIcon = (
    <SvgIcon component={UncheckedIcon} inheritViewBox sx={{ fontSize: '16px' }} color="inherit" />
)
const CheckboxCheckedIcon = <SvgIcon component={CheckedIcon} inheritViewBox sx={{ fontSize: '16px' }} color="inherit" />
const CheckboxIndeterminateIcon = (
    <SvgIcon component={IndeterminateIcon} inheritViewBox sx={{ fontSize: '16px' }} color="inherit" />
)

const MENU_MAX_HEIGHT = '60vh'

const DEFAULT_MENU_WIDTH = 240

const SEARCH_THRESHOLD = 10

const MenuActionButton = ({
    children,
    isDisabled = false,
    onClick,
}: {
    children: ReactNode
    isDisabled?: boolean
    onClick: () => void
}) => (
    <Button
        size="small"
        variant="outlined"
        disabled={isDisabled}
        onClick={onClick}
        sx={{
            flex: 1,
            px: 1,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            borderColor: 'figma.grayscale.stroke',
            color: 'figma.grayscale.textHier2',
            transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
            '&:hover': {
                borderColor: 'figma.primary',
                backgroundColor: 'figma.primary5',
                color: 'figma.primary',
            },
            '&.Mui-disabled': {
                borderColor: 'figma.grayscale.tier5',
                color: 'figma.grayscale.tier5',
            },
        }}
    >
        <Typography variant="14x600" color="inherit">
            {children}
        </Typography>
    </Button>
)

export interface NestedMultiselectFilterItem<TItemId = string> {
    id: TItemId
    label: string
    children?: NestedMultiselectFilterItem<TItemId>[]
}

export interface NestedMultiselectFilterProps<TItemId extends string = string> {
    icon: React.ElementType
    items: NestedMultiselectFilterItem<TItemId>[]
    selectedItemIds: TItemId[]
    onSelectionChange: (selectedItemIds: TItemId[]) => void
    /** Overrides the "not everything is selected" default, for callers whose filters start partly off. */
    isHighlighted?: boolean
    onReset?: () => void
    canReset?: boolean
    onOpenChange?: (open: boolean) => void
    /** Widens both panels for a caller whose labels are longer than the default fits. */
    menuWidth?: number
    getItemChildren?: (itemId: TItemId) => TItemId[]
    sx?: any
}

/**
 * A reusable nested multiselect filter component with popper-based UI.
 * Supports parent-child relationships with automatic selection synchronization.
 * Designed to be modular and reusable for different filter scenarios.
 */
const NestedMultiselectFilter = <TItemId extends string = string>({
    icon: Icon,
    items: liveItems,
    selectedItemIds,
    onSelectionChange,
    isHighlighted,
    onReset,
    canReset = true,
    onOpenChange,
    menuWidth = DEFAULT_MENU_WIDTH,
    getItemChildren,
    sx,
}: NestedMultiselectFilterProps<TItemId>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    // Snapshotted while the menu is open. An item that disappears from under the pointer as it is
    // clicked takes the menu with it: the click lands on a node React has already detached, so the
    // click-away check finds no menu above it and reads it as a click outside.
    const [openItems, setOpenItems] = useState(liveItems)
    const items = anchorEl ? openItems : liveItems
    const [hoveredItemId, setHoveredItemId] = useState<TItemId | null>(null)
    const [hoveredItemEl, setHoveredItemEl] = useState<null | HTMLElement>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const selectedSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds])

    const allItemIds = useMemo(
        () => items.flatMap((item) => (item.children?.length ? item.children.map((child) => child.id) : [item.id])),
        [items]
    )

    const isEverythingSelected = allItemIds.length > 0 && allItemIds.every((itemId) => selectedSet.has(itemId))

    const handleToggleAll = () => onSelectionChange(isEverythingSelected ? [] : allItemIds)

    const hasOpenMenu = !!anchorEl || !!hoveredItemId || !!hoveredItemEl
    useEscapeKey(() => {
        setAnchorEl(null)
        setHoveredItemId(null)
        setHoveredItemEl(null)
    }, hasOpenMenu)

    const handleToggle = (event: MouseEvent<HTMLElement>) => {
        const next = anchorEl ? null : event.currentTarget

        if (next) {
            setOpenItems(liveItems)
            setSearchQuery('')
        }

        setAnchorEl(next)
        onOpenChange?.(!!next)
        if (anchorEl) {
            setHoveredItemId(null)
            setHoveredItemEl(null)
        }
    }

    const handleClose = () => {
        setAnchorEl(null)
        setHoveredItemId(null)
        setHoveredItemEl(null)
        onOpenChange?.(false)
    }

    const handleItemMouseEnter = (itemId: TItemId, element: HTMLElement) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }
        setHoveredItemId(itemId)
        setHoveredItemEl(element)
    }

    const handleItemMouseLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredItemId(null)
            setHoveredItemEl(null)
        }, 150)
    }

    const handleNestedPopperMouseEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }
    }

    const handleNestedPopperMouseLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredItemId(null)
            setHoveredItemEl(null)
        }, 150)
    }

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current)
            }
        }
    }, [])

    const getChildIds = useCallback(
        (itemId: TItemId): TItemId[] => {
            if (getItemChildren) {
                return getItemChildren(itemId)
            }
            const item = items.find((i) => i.id === itemId)
            return item?.children?.map((c) => c.id) || []
        },
        [items, getItemChildren]
    )

    const totalSelectableIds = useMemo(() => {
        return items.flatMap((item) => {
            const childIds = getChildIds(item.id)
            return childIds.length > 0 ? childIds : [item.id]
        })
    }, [items, getChildIds])

    const filtersApplied = isHighlighted ?? selectedSet.size !== totalSelectableIds.length
    const [withPulsingCircle] = useState(filtersApplied)

    /**
     * Handles parent item toggle (section).
     * When selecting a section, all its children (rows) are selected.
     * When unselecting a section, all its children are unselected.
     */
    const handleParentToggle = useCallback(
        (itemId: TItemId) => {
            const childIds = getChildIds(itemId)

            // If no children, toggle the item itself
            if (childIds.length === 0) {
                if (selectedSet.has(itemId)) {
                    onSelectionChange(selectedItemIds.filter((id) => id !== itemId))
                } else {
                    onSelectionChange([...selectedItemIds, itemId])
                }
                return
            }

            // Check if all children are currently selected
            const allChildrenSelected = childIds.every((id) => selectedSet.has(id))

            if (allChildrenSelected) {
                // All children are selected: unselect all children (unselect section)
                const newSelected = selectedItemIds.filter((id) => !childIds.includes(id))
                onSelectionChange(newSelected)
            } else {
                // Some or no children are selected: select all children (select section)
                const childrenToAdd = childIds.filter((id) => !selectedSet.has(id))
                const newSelected = [...selectedItemIds, ...childrenToAdd]
                onSelectionChange(newSelected)
            }
        },
        [selectedItemIds, selectedSet, getChildIds, onSelectionChange]
    )

    /**
     * Handles child item toggle (row).
     * When selecting a row, its parent section is automatically selected.
     * When unselecting all rows in a section, the section is automatically unselected.
     */
    const handleChildToggle = useCallback(
        (itemId: TItemId) => {
            const parentItem = items.find((item) => item.children?.some((c) => c.id === itemId))
            if (!parentItem) {
                // Toggle the item itself if it has no parent
                if (selectedSet.has(itemId)) {
                    onSelectionChange(selectedItemIds.filter((id) => id !== itemId))
                } else {
                    onSelectionChange([...selectedItemIds, itemId])
                }
                return
            }

            const isCurrentlySelected = selectedSet.has(itemId)

            if (isCurrentlySelected) {
                // Unselect child: remove from selection
                const newSelected = selectedItemIds.filter((id) => id !== itemId)
                onSelectionChange(newSelected)
            } else {
                // Select child: add to selection
                // Note: Parent selection is handled automatically via isParentSelected logic
                const newSelected = [...selectedItemIds, itemId]
                onSelectionChange(newSelected)
            }
        },
        [selectedItemIds, selectedSet, items, onSelectionChange]
    )

    /**
     * Determines if a parent item is selected.
     * A parent is considered selected if at least one of its children is selected.
     */
    const isParentSelected = useCallback(
        (itemId: TItemId): boolean => {
            const childIds = getChildIds(itemId)
            if (childIds.length === 0) {
                return selectedSet.has(itemId)
            }
            // Parent is selected if at least one child is selected
            return childIds.some((id) => selectedSet.has(id))
        },
        [selectedSet, getChildIds]
    )

    /**
     * Gets the selection count for a parent item.
     * Returns the number of selected children and total children.
     */
    const getParentSelectionCount = useCallback(
        (itemId: TItemId): { selected: number; total: number } => {
            const childIds = getChildIds(itemId)
            if (childIds.length === 0) {
                return { selected: selectedSet.has(itemId) ? 1 : 0, total: 1 }
            }
            const selectedCount = childIds.filter((id) => selectedSet.has(id)).length
            return { selected: selectedCount, total: childIds.length }
        },
        [selectedSet, getChildIds]
    )

    const visibleItems = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()

        return query ? items.filter((item) => item.label.toLowerCase().includes(query)) : items
    }, [items, searchQuery])

    const handleSearchChange = (value: string) => {
        setSearchQuery(value)
        setHoveredItemId(null)
        setHoveredItemEl(null)
    }

    const isSearchVisible = items.length > SEARCH_THRESHOLD

    const hoveredItem = hoveredItemId ? items.find((item) => item.id === hoveredItemId) : null
    const hasNestedItems = hoveredItem && hoveredItem.children && hoveredItem.children.length > 0

    const renderParentItem = (
        item: NestedMultiselectFilterItem<TItemId>,
        isSelected: boolean,
        selectedCount: number,
        totalCount: number,
        onToggle: () => void,
        isHovered: boolean
    ) => {
        const hasChildren = item.children && item.children.length > 0
        const isNestedOpen = isHovered && hasChildren
        const isPartiallySelected = selectedCount > 0 && selectedCount < totalCount

        return (
            <Stack
                onClick={onToggle}
                direction="row"
                gap={1.5}
                px={1.5}
                py={0.75}
                alignItems="center"
                sx={{
                    color: isSelected ? 'figma.primary' : 'figma.grayscale.textHier2',
                    '&:hover': {
                        color: 'figma.primary',
                    },
                    cursor: 'pointer',
                }}
            >
                <Checkbox
                    size="small"
                    checked={isSelected}
                    indeterminate={isPartiallySelected}
                    icon={CheckboxUncheckedIcon}
                    checkedIcon={CheckboxCheckedIcon}
                    indeterminateIcon={CheckboxIndeterminateIcon}
                    sx={{
                        pointerEvents: 'none',
                        p: 0,
                        color: 'inherit',
                    }}
                />
                <OverflowTooltipWrapper title={item.label} placement="top-start">
                    <Typography
                        variant="16x500"
                        color="inherit"
                        whiteSpace="nowrap"
                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', mt: -0.25 }}
                    >
                        {item.label}
                    </Typography>
                </OverflowTooltipWrapper>
                {hasChildren && totalCount > 0 && (
                    <>
                        <Typography
                            variant={selectedCount === totalCount ? '14x500' : '14x600'}
                            color={selectedCount === totalCount ? 'inherit' : 'figma.grayscale.tier4'}
                            whiteSpace="nowrap"
                        >
                            ({selectedCount}/{totalCount})
                        </Typography>
                        <SvgIcon
                            component={RightArrowIcon}
                            inheritViewBox
                            sx={{
                                fontSize: '16px',
                                color: (theme) => (isNestedOpen ? 'inherit' : theme.palette.figma.grayscale.textHier2),
                                transform: isNestedOpen ? 'rotateY(180deg)' : 'rotateY(0deg)',
                                transition: 'color 0.25s ease, transform 0.25s ease',
                            }}
                        />
                    </>
                )}
            </Stack>
        )
    }

    const renderChildItem = (item: NestedMultiselectFilterItem<TItemId>, isSelected: boolean, onToggle: () => void) => {
        return (
            <Stack
                onClick={onToggle}
                direction="row"
                gap={1.5}
                px={1.5}
                py={0.75}
                alignItems="center"
                sx={{
                    color: isSelected ? 'figma.primary' : 'figma.grayscale.textHier2',
                    '&:hover': {
                        color: 'figma.primary',
                    },
                    cursor: 'pointer',
                }}
            >
                <Checkbox
                    size="small"
                    checked={isSelected}
                    icon={CheckboxUncheckedIcon}
                    checkedIcon={CheckboxCheckedIcon}
                    sx={{
                        pointerEvents: 'none',
                        p: 0,
                        color: 'inherit',
                    }}
                />
                <OverflowTooltipWrapper title={item.label} placement="top-start">
                    <Typography
                        variant="16x500"
                        color="inherit"
                        whiteSpace="nowrap"
                        sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', mt: -0.25 }}
                    >
                        {item.label}
                    </Typography>
                </OverflowTooltipWrapper>
            </Stack>
        )
    }

    return (
        <Stack>
            <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                    p: 0.5,
                    color: filtersApplied ? 'figma.primary' : 'figma.grayscale.tier2',
                    ...sx,
                }}
            >
                {withPulsingCircle && <PulsingCircle duration={7500} size={6} />}
                <SvgIcon component={Icon} inheritViewBox fontSize="small" color="inherit" sx={{ fontSize: '20px' }} />
            </IconButton>
            <Popper
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="bottom-start"
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [16, 4],
                        },
                    },
                ]}
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={(event) => {
                            // Don't close if clicking inside parent popper, nested popper, or on checkboxes/icon buttons
                            const target = event.target as HTMLElement
                            if (
                                target.closest('[data-parent-popper]') ||
                                target.closest('[data-nested-popper]') ||
                                target.closest('.MuiCheckbox-root') ||
                                target.closest('.MuiIconButton-root')
                            ) {
                                return
                            }
                            handleClose()
                        }}
                    >
                        <Fade {...TransitionProps} in={!!anchorEl} timeout={800} unmountOnExit mountOnEnter>
                            <Stack
                                data-parent-popper
                                direction="column"
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                                ml={-1.25}
                                minWidth={menuWidth}
                                width={menuWidth}
                                sx={{ maxHeight: MENU_MAX_HEIGHT, overflow: 'hidden' }}
                            >
                                <Stack
                                    direction="row"
                                    gap={1}
                                    px={1.5}
                                    py={1.25}
                                    borderBottom="1px solid"
                                    borderColor="figma.grayscale.stroke"
                                    flexShrink={0}
                                >
                                    <MenuActionButton onClick={handleToggleAll}>
                                        {isEverythingSelected ? 'Deselect all' : 'Select all'}
                                    </MenuActionButton>
                                    {!!onReset && (
                                        <MenuActionButton isDisabled={!canReset} onClick={onReset}>
                                            Reset
                                        </MenuActionButton>
                                    )}
                                </Stack>
                                {isSearchVisible && (
                                    <Stack px={1.5} pt={1.25} pb={0.75} flexShrink={0}>
                                        <SearchInput
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            label="Search the list"
                                            width="100%"
                                            sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
                                        />
                                    </Stack>
                                )}
                                <Stack
                                    direction="column"
                                    pt={isSearchVisible ? 0 : 0.75}
                                    pb={0.75}
                                    sx={{ overflowY: 'auto', ...thinScrollbarSx }}
                                >
                                    {!visibleItems.length && (
                                        <Typography variant="14x400" color="figma.grayscale.tier3" px={1.5} py={0.75}>
                                            No matches
                                        </Typography>
                                    )}
                                    {visibleItems.map((item) => {
                                        const isSelected = isParentSelected(item.id)
                                        const { selected: selectedCount, total: totalCount } = getParentSelectionCount(
                                            item.id
                                        )
                                        const isHovered = hoveredItemId === item.id
                                        const handleToggle = () => {
                                            handleParentToggle(item.id)
                                        }

                                        return (
                                            <Stack
                                                key={String(item.id)}
                                                onMouseEnter={(e) => handleItemMouseEnter(item.id, e.currentTarget)}
                                                onMouseLeave={handleItemMouseLeave}
                                                sx={{
                                                    position: 'relative',
                                                }}
                                            >
                                                {renderParentItem(
                                                    item,
                                                    isSelected,
                                                    selectedCount,
                                                    totalCount,
                                                    handleToggle,
                                                    isHovered
                                                )}
                                            </Stack>
                                        )
                                    })}
                                </Stack>
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            {hasNestedItems && hoveredItemEl && (
                <Popper
                    open={!!hoveredItemId && !!hoveredItem}
                    anchorEl={hoveredItemEl}
                    placement="right-start"
                    sx={(theme) => ({ zIndex: theme.zIndex.modal + 11 })}
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [-6, 8], // Small horizontal offset, no vertical offset for top alignment
                            },
                        },
                        {
                            name: 'flip',
                            enabled: false,
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                        },
                    ]}
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} in={!!hoveredItemId && !!hoveredItem} timeout={200} unmountOnExit>
                            <Stack
                                data-nested-popper
                                onMouseEnter={handleNestedPopperMouseEnter}
                                onMouseLeave={handleNestedPopperMouseLeave}
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                                minWidth={menuWidth}
                                width={menuWidth}
                                sx={{ maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto', ...thinScrollbarSx }}
                            >
                                {hoveredItem?.children?.map((childItem) => {
                                    const isSelected = selectedSet.has(childItem.id)
                                    const handleToggle = () => {
                                        handleChildToggle(childItem.id)
                                    }

                                    return (
                                        <React.Fragment key={String(childItem.id)}>
                                            {renderChildItem(childItem, isSelected, handleToggle)}
                                        </React.Fragment>
                                    )
                                })}
                            </Stack>
                        </Fade>
                    )}
                </Popper>
            )}
        </Stack>
    )
}

export default NestedMultiselectFilter
